import type { EventArg, NavigationAction } from '@react-navigation/native';
import { useNavigation } from 'expo-router';
import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

const ALLOWED_ACTIONS = ['GO_BACK', 'POP', 'POP_TO', 'REPLACE'];

export function useUnfocusCallback(
  callback: (
    event?: EventArg<'beforeRemove', true, { action: NavigationAction }>,
  ) => void,
) {
  const navigation = useNavigation();
  const callbackRef = useRef(callback);
  const isPreventedRef = useRef(false);
  const hasCalledCallbackRef = useRef(false);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', (event) => {
      if (
        !isPreventedRef.current &&
        ALLOWED_ACTIONS.includes(event.data.action.type)
      ) {
        isPreventedRef.current = true;
        hasCalledCallbackRef.current = true;
        event.preventDefault();

        callbackRef.current(event);

        if (Platform.OS === 'web' && !navigation.canGoBack()) {
          history.back();
        } else {
          requestAnimationFrame(() => {
            navigation.goBack();
          });
        }

        setTimeout(() => {
          isPreventedRef.current = false;
        }, 100);
      }
    });

    return unsubscribe;
  }, [navigation]);

  // On web, fire the callback when the component actually unmounts without
  // beforeRemove having fired — this covers browser back/forward button navigation
  // where the navigation stack is driven by the URL rather than in-app actions.
  // This is a separate effect ([] deps) so it only runs on true unmount, not when
  // the `navigation` reference changes due to a stack state update (e.g. pushing a
  // new screen on top), which would incorrectly fire the callback mid-session.
  useEffect(() => {
    return () => {
      if (Platform.OS === 'web' && !hasCalledCallbackRef.current) {
        callbackRef.current();
      }
    };
  }, []);
}
